import { type ReactNode } from 'react'
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { NuqsAdapter } from 'nuqs/adapters/react-router/v8'
import { Toaster } from 'sonner'
import { SessionProvider } from '@/auth/SessionProvider'
import { TooltipProvider } from '@/components/ui/tooltip'
import { isApiError } from '@/api/errors'
import { toastApiError } from '@/components/toasts'
import '@/i18n'

/**
 * Central query/mutation error policy (UI-RECOMMENDATIONS §10.3): the wrapper
 * handles the generic classes; screens opt out with meta.silentError and add
 * their own copy where they know better.
 */
function shouldRetry(failureCount: number, error: unknown): boolean {
  if (failureCount >= 2) return false
  if (isApiError(error)) {
    // Retry only transient classes — never client errors.
    return error.status === 0 || error.status >= 500
  }
  return false
}

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        retry: shouldRetry,
        staleTime: 30_000,
        refetchOnWindowFocus: false,
      },
    },
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (query.meta?.['silentError']) return
        toastApiError(error)
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        if (mutation.meta?.['silentError']) return
        // Screens that surface field-level or re-auth handling set silentError.
        toastApiError(error)
      },
    }),
  })
}

const queryClient = createQueryClient()

export function AppProviders({ children }: { children: ReactNode }) {
  return (
    <QueryClientProvider client={queryClient}>
      <NuqsAdapter>
        <SessionProvider>
          <TooltipProvider>
            {children}
            <Toaster position="top-right" richColors closeButton />
          </TooltipProvider>
        </SessionProvider>
      </NuqsAdapter>
    </QueryClientProvider>
  )
}
